import React from "react";
import { DropdownOption, FormElementType } from "core/models";

export interface Rules {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    isEmail?: boolean;
    isPhone?: boolean;
    isNumeric?: boolean;
    isEmpty?: boolean;
    isMatchingPattern?: string;
    isSameAsReference?: string;
}
export interface FormField {
    valid: boolean;
    touched: boolean;
    value: string | string[] | File | undefined | boolean | null;
    errorMsg: string;
    rules: Rules;
    title?: string;
    defaultText?: string;
    type?: FormElementType;
    options?: DropdownOption[];
    initialCheck?: boolean;
}
export interface FormState {
    [key: string]: FormField;
}
interface HookReturn {
    formValid: boolean;
    formState: FormState;
    inputChangeHandler(
        inputIdentifier: string,
        value: string | string[] | File | undefined | boolean,
    ): void;
}

export const checkValidity = (
    rules: Rules,
    value: string | File,
    reference?: string,
): [boolean, string] => {
    let isValid = true;
    let errorMsg = "";
    if (!rules || value instanceof File) {
        return [true, ""];
    }
    if (rules.minLength) {
        isValid = value.length >= rules.minLength && isValid;
        if (value.length < rules.minLength) {
            errorMsg = `This field requires at least ${rules.minLength} characters`;
        }
    }
    if (rules.required) {
        if (Array.isArray(value)) {
            isValid = value.length !== 0 && isValid;
            if (value.length <= 0) {
                errorMsg = "This field is required";
            }
        } else {
            isValid = value.trim() !== "" && isValid;
            if (value.trim() === "") {
                errorMsg = "This field is required";
            }
        }
    }
    if (rules.maxLength) {
        isValid = value.length <= rules.maxLength && isValid;
        if (value.length > rules.maxLength) {
            errorMsg = `This field can have max ${rules.maxLength} characters`;
        }
    }
    if (rules.isEmail) {
        const pattern =
            /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
        isValid = pattern.test(value) && isValid;
        if (!pattern.test(value)) {
            errorMsg = `This needs to be a valid e-mail address`;
        }
    }
    if (rules.isPhone) {
        // eslint-disable-next-line no-useless-escape
        const pattern = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;
        isValid = pattern.test(value) && isValid;
        if (!pattern.test(value)) {
            errorMsg = `This needs to be a valid phone number`;
        }
    }
    if (rules.isNumeric) {
        const pattern = /^\d+$/;
        isValid = pattern.test(value) && isValid;
        if (!pattern.test(value)) {
            errorMsg = `This can only be a number`;
        }
    }
    if (rules.isSameAsReference) {
        isValid = value === reference && isValid;
        if (value !== reference) {
            errorMsg = "Fields do not match";
        }
    }
    if (rules.isEmpty) {
        isValid = value !== "" && isValid;
        if (!value) {
            errorMsg = "This cannot be empty";
        }
    }
    if (rules.isMatchingPattern) {
        const pattern = new RegExp(rules.isMatchingPattern);
        isValid = pattern.test(value) && isValid;
        if (!pattern.test(value)) {
            errorMsg = "Field did not match the pattern";
        }
    }
    return [isValid, errorMsg];
};

export const useFormValidation = (initialFormState: FormState): HookReturn => {
    const [formValid, setFormValid] = React.useState<boolean>(false);
    const [formState, setFormState] =
        React.useState<FormState>(initialFormState);
    const [initialCheck, setInitialCheck] = React.useState<string>("");

    const inputChangeHandler = (
        inputIdentifier: string,
        value: string | string[] | File | boolean,
    ) => {
        const updatedFormState = { ...formState };
        const updatedFormElement = { ...updatedFormState[inputIdentifier] };
        if (value instanceof File) {
            updatedFormElement.value = value;
        } else {
            updatedFormElement.value = value.toString();
        }
        const refcheck = (): string | undefined => {
            const reference =
                updatedFormState[inputIdentifier].rules.isSameAsReference;
            if (!reference) {
                return undefined;
            }
            return updatedFormState[reference].value?.toString();
        };
        const [validity, errorMsg] =
            updatedFormElement.type !== "file"
                ? checkValidity(
                      updatedFormState[inputIdentifier].rules,
                      updatedFormElement.value,
                      refcheck(),
                  )
                : [true, ""];
        updatedFormElement.valid = validity;
        updatedFormElement.errorMsg = errorMsg;
        updatedFormElement.touched = true;
        updatedFormState[inputIdentifier] = updatedFormElement;
        if (Array.isArray(value)) {
            // Turn value back into an Array if applicable
            updatedFormElement.value = value;
        }
        let formIsValid = true;
        const updatedFormStateKeys = Object.keys(updatedFormState);
        updatedFormStateKeys.forEach((key): void => {
            formIsValid = updatedFormState[key].valid && formIsValid;
        });

        setFormValid(formIsValid);
        setFormState(updatedFormState);
    };

    // eslint-disable-next-line consistent-return
    React.useEffect(() => {
        if (initialCheck === "" || initialCheck !== "__all_checked") {
            const keys = Object.keys(initialFormState);
            const current =
                initialCheck === ""
                    ? keys[0]
                    : keys[keys.indexOf(initialCheck) + 1];
            if (current === undefined) {
                return setInitialCheck("__all_checked");
            }
            if (
                initialFormState[current].value !== "" ||
                initialFormState[current].initialCheck === true
            ) {
                inputChangeHandler(
                    current,
                    initialFormState[current].value || "",
                );
                setInitialCheck(current);
            }
        }
    }, []);

    return {
        formValid,
        formState,
        inputChangeHandler,
    };
};
